import { CACHE_PREFIX } from './serviceWorker';

export const SERVICE_WORKER_URL = '/sw.js';

export type RegisterOutcome = 'registered' | 'unregistered' | 'unsupported' | 'skipped' | 'failed';

export interface RegisterServiceWorkerOptions {
  enabled: boolean;
  container?: Pick<ServiceWorkerContainer, 'register' | 'getRegistrations'>;
  cacheStorage?: Pick<CacheStorage, 'keys' | 'delete'>;
  /** Resolves once the page has loaded, so the precache never competes with the first load. */
  whenLoaded?: () => Promise<void>;
  isNativeApp?: () => boolean;
}

interface CapacitorGlobal {
  Capacitor?: { isNativePlatform?: () => boolean };
}

// The native app (VOL-14 lot 4) loads its files from the package: a worker would only
// keep stale copies of them.
const isCapacitorNative = (): boolean =>
  (globalThis as CapacitorGlobal).Capacitor?.isNativePlatform?.() === true;

const pageLoaded = (): Promise<void> =>
  document.readyState === 'complete'
    ? Promise.resolve()
    : new Promise((resolve) => window.addEventListener('load', () => resolve(), { once: true }));

const isOurs = (registration: ServiceWorkerRegistration): boolean =>
  [registration.active, registration.waiting, registration.installing].some(
    (worker) => worker !== null && new URL(worker.scriptURL).pathname === SERVICE_WORKER_URL,
  );

export async function registerServiceWorker({
  enabled,
  container = typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined,
  cacheStorage = typeof caches !== 'undefined' ? caches : undefined,
  whenLoaded = pageLoaded,
  isNativeApp = isCapacitorNative,
}: RegisterServiceWorkerOptions): Promise<RegisterOutcome> {
  // Absent on http:// outside localhost and in some private modes.
  if (!container) return 'unsupported';
  if (isNativeApp()) return 'skipped';
  try {
    if (enabled) {
      await whenLoaded();
      await container.register(SERVICE_WORKER_URL, { scope: '/' });
      return 'registered';
    }
    // Option turned off after a release that had it: without this, browsers that installed
    // the worker would keep serving its shell.
    const registrations = await container.getRegistrations();
    await Promise.all(registrations.filter(isOurs).map((registration) => registration.unregister()));
    if (cacheStorage) {
      const names = await cacheStorage.keys();
      await Promise.all(names.filter((name) => name.startsWith(CACHE_PREFIX)).map((name) => cacheStorage.delete(name)));
    }
    return 'unregistered';
  } catch {
    // The app works the same without the worker, only the offline start is lost.
    return 'failed';
  }
}
